/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { KTCard, KTSVG } from '../../../../../../../../_metronic/helpers'
import {
  KTable,
  KTableHeader,
  KTableLoading,
  KTableSelectionCellNonMultiple,
} from '../../../../../../components/UI'
import { KTableProvider, useKTable } from '../../../../../../contexts/KTableProvider'
import { useKhipu } from '../../../../../../hooks/useKhipu'
import { useLimpieza } from '../../../../../../hooks/useLimpieza'
import { TabLimpiezaModel } from '../../../../../../models'
import { useCampaments } from '../../../../hooks/central/useCampaments'
import { CLEANING_SCHEDULE_SERVICES } from '../../../../services'
import { ServiceSchedulingDto } from '../../../../model/cleaningSchedule.model'
import { format, isSameDay } from 'date-fns'
import es from 'date-fns/locale/es'
import { KTableHeaderMulti } from '../../../../../../components/UI/Table/KTableHeaderMulti'

const TableCleaningSchedule = () => {
  const {
    data,
    setData,
    setPagination,
    loading,
    setLoading,
    pagination,
    clearSelected,
    selectedOn,
    updateTable,
    setClearTable,
    setUpdateTable,
  } = useKTable()
  const { changeCurrentTabItem, currentTabItem } = useLimpieza()

  const { searchValues, clearSearchValues } = useKhipu()
  const { getCampaments, campaments } = useCampaments()

  const getCleaningSchedules = async () => {
    try {
      setLoading(true)
      const { data: response } = await CLEANING_SCHEDULE_SERVICES.filterCleaningSchedule({
        pageNumber: pagination.currentPage,
        size: pagination.perPage,
        ...searchValues,
      })
      if (!response.error) {
        setData(response.data)
        setPagination({
          ...pagination,
          totalResults: response.total,
        })
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentTabItem.loadingTable || updateTable) {
      clearSelected()
      getCleaningSchedules()
      setUpdateTable(false)
      setClearTable(false)
      changeCurrentTabItem({ ...currentTabItem, loadingTable: false })
    } else {
      getCleaningSchedules()
    }
  }, [pagination.currentPage, currentTabItem.loadingTable, updateTable])

  useEffect(() => {
    getCampaments({ isCombo: true }, 1)
    return () => {
      clearSearchValues()
    }
  }, [])

  const handleViewPanel = (data: any) => {
    const obj: TabLimpiezaModel = {
      ...currentTabItem,
      showPanel: true,
      idToEdit: data.Id,
      idToDelete: '',
      detailRow: data,
      content: {
        ...currentTabItem.content,
        detailPanel: 'tab-cleaning-schedule',
      },
      currentExternalTab: 0,
    }
    changeCurrentTabItem(obj)
    clearSelected()
  }

  const handleGetId = (id: any) => {
    const idAsString = id ? String(id) : ''
    const idChange = idAsString === currentTabItem.idToDelete ? '' : idAsString
    const obj: TabLimpiezaModel = {
      ...currentTabItem,
      showPanel: false,
      idToDelete: idChange,
      idToEdit: '',
    }
    changeCurrentTabItem(obj)
  }

  const columns: Column<ServiceSchedulingDto>[] = useMemo(
    () => [
      {
        Header: (props) => <KTableHeader tableProps={props} />,
        id: 'selection',
        Cell: ({ ...props }) => (
          <KTableSelectionCellNonMultiple
            id={props.data[props.row.index].Id}
            clickHandler={handleGetId}
          />
        ),
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='Campamento'
            name={'CampamentoId'}
            selecteable
            data={campaments}
          />
        ),
        accessor: 'Campamento',
      },
      {
        Header: (props) => (
          <KTableHeader tableProps={props} title='Programacion de Limpieza' name='Nomenclatura' searcheable />
        ),
        accessor: 'Nomenclatura',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='TipoEdificacion'
            name='TipoEdificacion'
            searcheable
          />
        ),
        accessor: 'TipoEdificacion',
      },
      {
        Header: (props) => (
          <KTableHeader
            tableProps={props}
            title='TipoRepeticion'
            name='TipoRepeticion'
            searcheable
          />
        ),
        accessor: 'TipoRepeticion',
      },
      {
        Header: (props) => (
          <KTableHeaderMulti
            tableProps={props}
            fields={[
              { name: 'FechaInicio', label: 'Fecha inicio', type: 'date' },
              { name: 'FechaFin', label: 'Fecha fin', type: 'date' },
              { name: 'HoraInicio', label: 'Hora inicio', type: 'time' },
            ]}
          />
        ),
        id: 'fechas',
        Cell: ({ row }) => {
          const { FechaInicio, FechaFin, HoraInicio } = row.original || {}

          if (!FechaInicio) {
            return <div className='text-center text-muted'>Sin fecha</div>
          }

          const fechaInicio = new Date(FechaInicio)
          const fechaFin = FechaFin ? new Date(FechaFin) : null

          if (HoraInicio) {
            const [h = 0, m = 0, s = 0] = HoraInicio.split(':')
            fechaInicio.setHours(Number(h), Number(m), Number(s))
          }

          const mismoDia = fechaFin && isSameDay(fechaInicio, fechaFin)

          return (
            <div className='d-flex justify-content-center flex-shrink-0'>
              {fechaFin
                ? mismoDia
                  ? format(fechaInicio, 'dd MMM yyyy • HH:mm', { locale: es })
                  : `Del ${format(fechaInicio, 'dd MMM yyyy HH:mm', { locale: es })} al ${format(
                    fechaFin,
                    'dd MMM yyyy',
                    { locale: es },
                  )}`
                : format(fechaInicio, 'dd MMM yyyy HH:mm', { locale: es })}
            </div>
          )
        },
      },
      {
        Header: (props) => <KTableHeader tableProps={props} title='Ver' />,
        id: 'actions',
        Cell: ({ ...props }) => (
          <div
            className='d-flex justify-content-center flex-shrink-0'
            onClick={() => handleViewPanel(props.row.original)}
          >
            <a href='#' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'>
              <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-3' />
            </a>
          </div>
        ),
      },
    ],
    [campaments, selectedOn],
  )

  return (
    <KTCard shadow className='m-5'>
      <KTable data={data} columns={columns as any} showPageSizeSelector />
      {loading ? <KTableLoading title='Cargando...' /> : null}
    </KTCard>
  )
}

export const CleaningScheduleTable = () => {
  return (
    <KTableProvider>
      <TableCleaningSchedule />
    </KTableProvider>
  )
}
